import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, skip } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { ApiClient, type CartPayloadDto, type SavedCartTemplateSummaryDto } from '../api/api.client';
import { AuthService } from '../auth/auth.service';
import { CartService } from './cart.service';
import { migrateCartState, type CartState } from './cart';

const PUSH_DEBOUNCE_MS = 800;

/**
 * Synchronise le panier local avec le compte client et gère les sélections enregistrées.
 */
@Injectable({ providedIn: 'root' })
export class CartSyncService {
  private readonly cart = inject(CartService);
  private readonly auth = inject(AuthService);
  private readonly api = inject(ApiClient);
  private readonly destroyRef = inject(DestroyRef);

  private pushTimer: ReturnType<typeof setTimeout> | undefined;
  private merging = false;

  constructor() {
    this.cart.onPersist((state) => {
      if (this.merging || !this.auth.isAuthenticated()) {
        return;
      }
      this.schedulePush(state);
    });

    toObservable(this.auth.isAuthenticated)
      .pipe(distinctUntilChanged(), skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((authed) => {
        if (authed) {
          void this.mergeFromServer();
          return;
        }
        this.cancelPush();
      });

    this.destroyRef.onDestroy(() => this.cancelPush());
  }

  async bootstrap(): Promise<void> {
    if (this.auth.isAuthenticated()) {
      await this.mergeFromServer();
    }
  }

  async listTemplates(): Promise<readonly SavedCartTemplateSummaryDto[]> {
    const response = await firstValueFrom(this.api.listSavedCartTemplates());
    return response.templates;
  }

  async saveTemplate(label?: string): Promise<'saved' | 'full' | 'empty' | 'error'> {
    const state = this.cart.cart();
    if (!state) {
      return 'empty';
    }
    if (!this.auth.isAuthenticated()) {
      return 'error';
    }
    try {
      await firstValueFrom(this.api.saveCartTemplate({ cart: state as unknown as CartPayloadDto, label }));
      return 'saved';
    } catch (error) {
      if (isTemplatesFullError(error)) {
        return 'full';
      }
      return 'error';
    }
  }

  async loadTemplate(templateId: string): Promise<boolean> {
    if (!this.auth.isAuthenticated()) {
      return false;
    }
    try {
      const response = await firstValueFrom(this.api.getSavedCartTemplate(templateId));
      this.merging = true;
      try {
        const migrated =
          migrateCartState(response.template.cart) ??
          (response.template.cart as unknown as CartState);
        this.cart.replaceState(migrated);
      } finally {
        this.merging = false;
      }
      await this.push(this.cart.cart());
      return true;
    } catch {
      return false;
    }
  }

  async deleteTemplate(templateId: string): Promise<boolean> {
    try {
      await firstValueFrom(this.api.deleteSavedCartTemplate(templateId));
      return true;
    } catch {
      return false;
    }
  }

  async saveGuestCartByEmail(email: string): Promise<'sent' | 'empty' | 'error'> {
    const state = this.cart.cart();
    if (!state) {
      return 'empty';
    }
    try {
      await firstValueFrom(this.api.saveGuestCart({ email, cart: state as unknown as CartPayloadDto }));
      return 'sent';
    } catch {
      return 'error';
    }
  }

  async restoreGuestCart(snapshotId: string, token: string): Promise<boolean> {
    try {
      const response = await firstValueFrom(this.api.getGuestCart(snapshotId, token));
      this.merging = true;
      try {
        const migrated =
          migrateCartState(response.cart) ?? (response.cart as unknown as CartState);
        this.cart.replaceState(migrated);
      } finally {
        this.merging = false;
      }
      return true;
    } catch {
      return false;
    }
  }

  private schedulePush(state: CartState | null): void {
    this.cancelPush();
    this.pushTimer = setTimeout(() => {
      void this.push(state);
    }, PUSH_DEBOUNCE_MS);
  }

  private cancelPush(): void {
    if (this.pushTimer !== undefined) {
      clearTimeout(this.pushTimer);
      this.pushTimer = undefined;
    }
  }

  private async push(state: CartState | null): Promise<void> {
    if (!this.auth.isAuthenticated()) {
      return;
    }
    try {
      if (state === null) {
        await firstValueFrom(this.api.clearActiveCart());
      } else {
        await firstValueFrom(this.api.putActiveCart(state as unknown as CartPayloadDto));
      }
    } catch {
      // La sync reprendra au prochain changement local.
    }
  }

  private async mergeFromServer(): Promise<void> {
    if (!this.auth.isAuthenticated()) {
      return;
    }
    this.merging = true;
    try {
      const remote = await firstValueFrom(this.api.getActiveCart());
      const local = this.cart.cart();
      if (!remote.cart && !local) {
        return;
      }
      if (remote.cart && !local) {
        const migrated =
          migrateCartState(remote.cart) ?? (remote.cart as unknown as CartState);
        this.cart.replaceState(migrated);
        return;
      }
      if (!remote.cart && local) {
        await this.push(local);
        return;
      }
      if (remote.cart && local) {
        const remoteTime = Date.parse(remote.cart.updatedAt);
        const localTime = Date.parse(local.updatedAt);
        if (remoteTime >= localTime) {
          const migrated =
          migrateCartState(remote.cart) ?? (remote.cart as unknown as CartState);
          this.cart.replaceState(migrated);
        } else {
          await this.push(local);
        }
      }
    } catch {
      // Panier local conservé.
    } finally {
      this.merging = false;
    }
  }
}

function isTemplatesFullError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'error' in error &&
    typeof (error as { error?: { code?: string } }).error?.code === 'string' &&
    (error as { error: { code: string } }).error.code === 'TEMPLATES_FULL'
  );
}
